import { React } from "react";
import { makeStyles } from "@mui/styles";
import {
  Card,
  Grid,
  Input,
  TableContainer,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Container,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DoneIcon from "@mui/icons-material/DoneAll";
import RevertIcon from "@mui/icons-material/HighlightOff";
import AddNew from "@mui/icons-material/AddCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Box } from "@mui/system";

const DataType = {
  String: 0,
  Numeric: 1,
};

const CustomTableCell = ({
  rowIndex,
  row,
  name,
  onChange,
  isEditableField,
  dataType,
}) => {
  const useStyles = makeStyles((themes) => ({
    input: {
      width: 100,
      height: 30,
    },
  }));
  const classes = useStyles();
  const { isEditMode } = row;

  let finalValue = row[name] ? row[name] : "--";

  if (
    finalValue &&
    [
      "fieldGoalsPct",
      "twoPointsPct",
      "threePointsPct",
      "freeThrowsPct",
      "trueShootingPct",
    ].includes(name)
  ) {
    finalValue = Math.round(row[name] * 100);
  }

  //#Disable Field Goal percentage and show auto-calculated percentage default.
  const disableProps = [
    "fieldGoalsPct",
    "twoPointsPct",
    "threePointsPct",
    "freeThrowsPct",
  ].includes(name);

  return (
    <TableCell align="left" className={classes.tableCell}>
      {isEditMode && isEditableField ? (
        <Input
          value={finalValue || ""}
          name={name}
          onChange={(e) =>
            onChange(rowIndex, name, e.target.value, dataType, row)
          }
          className={classes.input}
          type={`${dataType == DataType.Numeric ? "number" : "text"}`}
          step={dataType == DataType.Numeric ? "0.01" : ""}
          disabled={disableProps}
        />
      ) : (
        finalValue
      )}
    </TableCell>
  );
};

export default function PlayerCareerStatsBasicStats(props) {
  const {
    styles,
    data,
    onChange,
    canUpdateData,
    isEditOn,
    RevertEntireRow,
    saveStats,
    addNewRow,
    readOnlyData,
    refreshPlayerData,
  } = props;
  const StatsTypeBasic = 0;
  const useStyles = makeStyles((themes) => ({
    ...styles,
    table: {
      minWidth: 650,
    },
    selectTableCell: {
      width: 60,
    },
    tableCell: {
      width: 130,
      height: 40,
    },
    input: {
      width: 130,
      height: 40,
    },
    drgContainer: {
      overflowY: "auto",
      "&::-webkit-scrollbar": {
        width: "10px",
        height: "10px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "#bdbdbd",
      },
      "&::-webkit-scrollbar-thumb": {
        boxShadow: "inset 0 0 8px rgba(0, 0, 0, 0.7)",
      },
    },
    tbl: {
      "& thead tr th": {
        fontSize: "0.75rem",
      },
      "& tbody tr td": {
        fontSize: "0.75rem",
      },
      "& tbody tr:hover td:not([rowSpan])": {
        backgroundColor: "#d5e2fb",
        fontWeight: "bold",
      },
    },
    gridContainer: {
      // width: "calc(100vw - 259px)",
      overflow: "auto",
    },
  }));
  const classes = useStyles();
  return (
    <Paper sx={{ marginTop: "10px", overflow: "auto" }}>
      <Grid container>
        <Grid
          item
          lg={10}
          md={10}
          sm={10}
          xs={10}
          className={classes.sectionTitlePadding}
        >
          <Box display="flex" flexDirection="row" alignItems="center" p={1}>
            <Typography
              noWrap
              variant={styles.gridSectionTitle.variant}
              className={classes.gridSectionTitle}
            >
              Career Basic Stats
            </Typography>
            {canUpdateData && readOnlyData && readOnlyData.sportRadarPlayerID && (
              <Tooltip title="Refresh data from Sports Radar">
                <IconButton
                  onClick={() =>
                    refreshPlayerData(
                      readOnlyData.playerID,
                      readOnlyData.sportRadarPlayerID
                    )
                  }
                  disabled={isEditOn}
                  color="success"
                  sx={{ fontSize: 30 }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Grid>
        <Grid
          item
          lg={12}
          md={12}
          sm={12}
          xs={12}
          className={classes.sectionTitlePadding}
        >
          {/* <Box className={classes.gridContainer}> */}
          <TableContainer
          // className={classes.drgContainer}
          >
            <Table
              className={`${classes.table} ${classes.tbl}`}
              aria-label="caption table"
            >
              <TableHead>
                <TableRow>
                  {canUpdateData && (
                    <TableCell align="left">
                      <Tooltip title="Add New Recrod">
                        <IconButton
                          onClick={() => addNewRow()}
                          disabled={isEditOn}
                        >
                          <AddNew />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell>Season Year</TableCell>
                  <TableCell>Height</TableCell>
                  <TableCell>Weight</TableCell>
                  <TableCell>Position</TableCell>
                  <TableCell>Jersey#</TableCell>
                  <TableCell>Experience</TableCell>
                  <TableCell>GP</TableCell>
                  <TableCell>GS</TableCell>
                  <TableCell>Min</TableCell>
                  <TableCell>FGM</TableCell>
                  <TableCell>FGA</TableCell>
                  <TableCell>FG%</TableCell>
                  <TableCell>2PM</TableCell>
                  <TableCell>2PA</TableCell>
                  <TableCell>2P%</TableCell>
                  <TableCell>3PM</TableCell>
                  <TableCell>3PA</TableCell>
                  <TableCell>3P%</TableCell>
                  <TableCell>FTM</TableCell>
                  <TableCell>FTA</TableCell>
                  <TableCell>FT%</TableCell>
                  <TableCell>ORB</TableCell>
                  <TableCell>DRB</TableCell>
                  <TableCell>REB</TableCell>
                  <TableCell>AST</TableCell>
                  <TableCell>TO</TableCell>
                  <TableCell>STL</TableCell>
                  <TableCell>BLK</TableCell>
                  <TableCell>PF</TableCell>
                  <TableCell>TF</TableCell>
                  <TableCell>PTS</TableCell>
                  <TableCell>FF</TableCell>
                  <TableCell>TS%</TableCell>
                </TableRow>
              </TableHead>
              {data != null && data.length > 0 && (
                <TableBody>
                  {data.map((obj, index) => (
                    <TableRow
                      key={`tblBasicStats_${index}`}
                      //title={`${obj.isSeasonEditable ? "Data created by Admin" : obj.isUpdated ? "Sports Radar data updated by Admin" : ""}`}
                      //style={{borderLeft: `5px solid ${obj.isSeasonEditable ? "#228b22" : obj.isUpdated ? "#ff7f50" : "transparent"}`}}
                    >
                      {canUpdateData && (
                        <TableCell align="left">
                          {obj.isEditMode ? (
                            <Box display="flex" flexDirection="row">
                              <Tooltip title="Save">
                                <IconButton
                                  aria-label="done"
                                  onClick={() => saveStats(index)}
                                  color="success"
                                  sx={{ fontSize: 30 }}
                                >
                                  <DoneIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton
                                  aria-label="revert"
                                  onClick={() => RevertEntireRow(index)}
                                  color="secondary"
                                >
                                  <RevertIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Tooltip title="Edit">
                              <IconButton
                                onClick={() =>
                                  props.SetEditMode(StatsTypeBasic, index)
                                }
                                disabled={isEditOn}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "seasonYear",
                          onChange,
                          isEditableField: obj.isSeasonEditable,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "height",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "weight",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "position",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.String,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "jerseyNumber",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.String,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "experience",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.String,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "gamesPlayed",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "gamesStarted",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "minutes",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "fieldGoalsMade",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "fieldGoalsAtt",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "fieldGoalsPct",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "twoPointsMade",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "twoPointsAtt",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "twoPointsPct",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "threePointsMade",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "threePointsAtt",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "threePointsPct",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "freeThrowsMade",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "freeThrowsAtt",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "freeThrowsPct",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "offensiveRebounds",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "defensiveRebounds",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "rebounds",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "assists",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "turnovers",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "steals",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "blocks",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "personalFouls",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "techFouls",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "points",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "flagrantFouls",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                      <CustomTableCell
                        {...{
                          rowIndex: index,
                          row: obj,
                          name: "trueShootingPct",
                          onChange,
                          isEditableField: true,
                          dataType: DataType.Numeric,
                        }}
                      />
                    </TableRow>
                  ))}
                </TableBody>
              )}
              {data != null && data.length == 0 && (
                <TableBody>
                  <TableRow key={`tblBasicStats_${0}`}>
                    {canUpdateData && <TableCell></TableCell>}
                    <TableCell colSpan="33">
                      <Typography variant="subtitle2" color="secondary">
                        No data found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
          {/* </Box> */}
        </Grid>
      </Grid>
    </Paper>
  );
}
